import axios from '@/plugins/axios';
import qs from 'qs';
import utils from '@/stores/utils';
import store from '@/stores/store';
import receipts from './submodules/receipts';
import $utils from '@/utils';
import { executeBulkAction as executeBulk } from "@/utils/bulk";
import { withSelectedIds } from "@/utils/selectionStats";
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const {
    getStorageKey,
    loadFiltersFromStorage,
    saveFiltersToStorage,
    mergeUserAndSectionFilters,
    extractUserFiltersFromEffective,
    getSectionConstraintKeys,
    omitFilterKeys,
} = utils;

/** Shared across all receipt tabs (section constraints applied at query time). */
const RECEIPTS_FILTERS_STORAGE_KEY = getStorageKey('receipts');
const DEFAULT_USER_FILTERS = { limit: 20, ordering: '-receipt_date' };

const state = {
    section: null,

    receipts: [],
    /** User-chosen filters persisted across tabs (no section constraints). */
    receipts_user_filters: { ...DEFAULT_USER_FILTERS },
    /** Effective filters for API (user + section overlay). */
    receipts_filters: { limit: 20, offset: 0, ordering: '-receipt_date' },
    receipts_user_filters_hydrated: false,
    receipts_count: 0,
    receipts_loaded: false,
    receipts_loading: false,
    receipts_loading_errors: null,
    receipts_cancel_source: null,

    selected_items: [],
    bulk_action_loading: false,

    receipts_stats: {},
    receipts_stats_loading: false,
    receipts_stats_loading_errors: null,
    receipts_stats_cancel_source: null,

};

/**
 * Hard tab constraints only — do not put soft defaults (e.g. exclude_readonly: false)
 * here or they would always win over user prefs.
 */
let getSectionFilters = function (section) {
    if (section == 'selfhandling_waiting') {
        return {
            ready: true,
            readonly: false,
            status: 'waiting',
            exclude_readonly: true,
            delegated: false,
        };
    } else if (section == 'selfhandling_toplace') {
        return {
            ready: true,
            readonly: true,
            placed: false,
            exclude_readonly: false,
            delegated: false,
            canceled: false,
        };

    } else if (section == 'selfhandling_processing') {
        return {
            ready: true,
            readonly: false,
            status__neq: 'waiting',
            exclude_readonly: true,
            delegated: false,
        };
    } else if (section == 'delegated') {
        return {
            ready: true,
            readonly: false,
            exclude_readonly: true,
            delegated: true,
        };
    } else if (section == 'delegated_waiting') {
        return {
            ready: true,
            readonly: false,
            status: 'waiting',
            exclude_readonly: true,
            delegated: true,
        };
    } else if (section == 'delegated_processing') {
        return {
            ready: true,
            readonly: false,
            status__neq: 'waiting',
            exclude_readonly: true,
            delegated: true,
        };
    } else if (section == 'delegated_completed') {
        return {
            ready: true,
            readonly: true,
            exclude_readonly: false,
            delegated: true,
            canceled: false,
        };

    } else if (section == 'drafts') {
        return {
            ready: false,
            readonly: false,
        };
    } else if (section == 'waiting') {
        return {
            ready: true,
            readonly: false,
            status: "waiting",
            // Override shared status chips so they cannot empty this tab.
            contextual_status: "to_be_processed,waiting",
        };
    } else if (section == 'processing') {
        return {
            ready: true,
            readonly: false,
            status__neq: "waiting",
            contextual_status: "handling",
        };
    } else if (section == 'toplace') {
        return {
            ready: true,
            readonly: true,
            placed: false,
            exclude_readonly: false,
            canceled: false,
            contextual_status: "complete,partial,missing",
        };
    } else if (section == 'all') {
        return {};
    } else if (section == 'completed') {
        // All closed receipts (complete / partial / missing / canceled).
        // contextual_status overrides shared status chips from other tabs.
        return {
            ready: true,
            readonly: true,
            exclude_readonly: false,
            contextual_status: "complete,partial,missing,canceled",
        };
    }

    return {};
};

/** Session counter key for a receipts tab — same keys as tab badges. */
const SECTION_STATS_KEYS = {
    all: 'receipts',
    drafts: 'draft_receipts',
    waiting: 'waiting_receipts',
    processing: 'processing_receipts',
    toplace: 'toplace_receipts',
    ready: 'ready_receipts',
    // No session counter yet for completed — always fetch the list.
    selfhandling_waiting: 'selfhandling_waiting_receipts',
    selfhandling_processing: 'selfhandling_processing_receipts',
    selfhandling_toplace: 'selfhandling_toplace_receipts',
    delegated: 'delegated_receipts',
    delegated_waiting: 'delegated_waiting_receipts',
    delegated_processing: 'delegated_processing_receipts',
    delegated_completed: 'delegated_completed_receipts',
};

const EMPTY_RECEIPTS_STATS = {
    receipts: 0,
    items_count: 0,
    items_total: 0,
    total_purchase_price_excl_vat: 0,
};

const getSectionReceiptsCount = (stats, section) => {
    if (!stats) {
        return null;
    }
    const key = SECTION_STATS_KEYS[section];
    if (!key) {
        return null;
    }
    const count = stats[key];
    return count == null ? null : count;
};

function normalizeReceiptUserStatus(filters) {
    const normalized = { ...filters };
    if (normalized.status != null) {
        if (normalized.contextual_status == null) {
            const statuses = Array.isArray(normalized.status)
                ? normalized.status
                : String(normalized.status).split(',');
            normalized.contextual_status = [...new Set(statuses.flatMap(status =>
                status === 'waiting' ? ['to_be_processed', 'waiting'] : [status]
            ))].join(',');
        }
        delete normalized.status;
    }
    return normalized;
}

function loadSharedReceiptsUserFilters(section) {
    const shared = loadFiltersFromStorage(RECEIPTS_FILTERS_STORAGE_KEY, null);
    const saved = shared || loadFiltersFromStorage(
        getStorageKey('receipts', section || 'all'),
        { ...DEFAULT_USER_FILTERS },
    );
    return normalizeReceiptUserStatus(extractUserFiltersFromEffective(
        saved,
        getSectionFilters(section || 'all'),
        {},
    ));
}

function persistReceiptsUserFilters(userFilters) {
    saveFiltersToStorage(RECEIPTS_FILTERS_STORAGE_KEY, userFilters);
}

function rebuildReceiptsFilters(state, { resetOffset = false } = {}) {
    const section = state.section || 'all';
    const previousOffset = state.receipts_filters?.offset ?? 0;
    state.receipts_filters = mergeUserAndSectionFilters(
        state.receipts_user_filters,
        getSectionFilters(section),
    );
    state.receipts_filters.offset = resetOffset ? 0 : previousOffset;
}

const mutations = {
    updatePendingReceiptsCount(state, count) {
        state.pending_receipts_count = count;
    },
    updateToPlaceReceiptsCount(state, count) {
        state.toplace_receipts_count = count;
    },
    updateReceipts(state, receipts) {
        state.receipts = receipts;
        // Replace selected_items instances with newly fetched receipts based on id field
        state.selected_items = state.selected_items?.map((selected_item) => {
            return receipts.find((receipt) => receipt.id === selected_item.id) || selected_item;
        }) || [];
    },
    updateSelectedItems(state, selected_items) {
        state.selected_items = selected_items || [];
    },
    updateSection(state, section) {
        state.section = section;
        state.receipts = [];
        state.receipts_count = 0;
        state.receipts_loaded = false;
        state.receipts_loading = true;

        if (!state.receipts_user_filters_hydrated) {
            state.receipts_user_filters = {
                ...DEFAULT_USER_FILTERS,
                ...loadSharedReceiptsUserFilters(section),
            };
            state.receipts_user_filters_hydrated = true;
            persistReceiptsUserFilters(state.receipts_user_filters);
        }
        rebuildReceiptsFilters(state, { resetOffset: true });
    },
    updateReceiptsCount(state, count) {
        state.receipts_count = count;
    },
    updateReceiptsLoading(state, loading) {
        state.receipts_loading = loading;
    },
    updateReceiptsLoaded(state, loaded) {
        state.receipts_loaded = loaded;
    },
    updateReceiptsLoadingErrors(state, errors) {
        state.receipts_loading_errors = errors;
    },
    updateReceiptsFilters(state, receipts_filters) {
        const section = state.section || 'all';
        state.receipts_user_filters = normalizeReceiptUserStatus(extractUserFiltersFromEffective(
            receipts_filters,
            getSectionFilters(section),
            state.receipts_user_filters,
        ));
        state.receipts_user_filters_hydrated = true;
        persistReceiptsUserFilters(state.receipts_user_filters);
        state.receipts_filters = mergeUserAndSectionFilters(
            state.receipts_user_filters,
            getSectionFilters(section),
        );
        state.receipts_filters.offset = receipts_filters?.offset ?? 0;
    },
    updateReceiptsCancelSource(state, source) {
        state.receipts_cancel_source = source;
    },
    updateReceiptsStats(state, receipts_stats) {
        state.receipts_stats = receipts_stats;
    },
    updateReceiptsStatsLoading(state, loading) {
        state.receipts_stats_loading = loading;
    },
    updateReceiptsStatsLoadingErrors(state, errors) {
        state.receipts_stats_loading_errors = errors;
    },
    updateReceiptsStatsCancelSource(state, source) {
        state.receipts_stats_cancel_source = source;
    },
    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = loading;
    },

};


const actions = {
    changeSection({ commit, dispatch, state }, section) {
        if (section == state.section && state.receipts_loaded) {
            return;
        }
        if (section != state.section) {
            commit('updateSection', section);
        } else {
            commit('updateReceiptsLoading', true);
            commit('updateReceiptsLoaded', false);
        }
        dispatch('fetchReceipts');
        dispatch('session/fetchStats', null, { root: true });
    },

    exportReceiptItemssAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters.receipt_ready = state.receipts_filters.ready;
            filters.receipt_readonly = state.receipts_filters.readonly;
            filters.receipt_status = state.receipts_filters.status;
            filters.ordering = state.receipts_filters.ordering;
            filters.exclude_readonly_receipts = state.receipts_filters.exclude_readonly;
            filters.receipt_date_min = state.receipts_filters.receipt_date_min;
            filters.receipt_date_max = state.receipts_filters.receipt_date_max;
            filters.provider = state.receipts_filters.provider;
            filters.customer = state.receipts_filters.customer;
            filters.stock = state.receipts_filters.stock;
            filters.search = state.receipts_filters.search;
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = 5000;
        
        $utils.downloadFile({
            url: "/api/receiptitems/",
            params: filters,
        }, $utils.getFilename('receiptItems'));
    },

    exportReceiptsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = Object.assign({}, state.receipts_filters);
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        
        $utils.downloadFile({
            url: "/api/receipts/",
            params: filters,
        }, $utils.getFilename('receipts'));
    },

    async fetchReceipts({ commit, dispatch, state, rootState }, params) {
        // Session tab counters already know this section is empty — skip list/stats requests.
        const sectionCount = getSectionReceiptsCount(
            rootState.session?.stats,
            state.section,
        );
        if (rootState.session?.stats_loaded && sectionCount === 0) {
            if (state.receipts_cancel_source) {
                state.receipts_cancel_source.cancel("canceled");
                commit('updateReceiptsCancelSource', null);
            }
            if (state.receipts_stats_cancel_source) {
                state.receipts_stats_cancel_source.cancel("canceled");
                commit('updateReceiptsStatsCancelSource', null);
            }
            commit('updateReceipts', []);
            commit('updateReceiptsCount', 0);
            commit('updateReceiptsLoading', false);
            commit('updateReceiptsLoaded', true);
            commit('updateReceiptsLoadingErrors', null);
            commit('updateReceiptsStats', { ...EMPTY_RECEIPTS_STATS });
            commit('updateReceiptsStatsLoading', false);
            return;
        }

        if (state.receipts_cancel_source) {
            state.receipts_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateReceiptsCancelSource', cancelSource);
        commit('updateReceiptsLoading', true);
        commit('updateReceiptsLoaded', false);
        commit('updateReceiptsLoadingErrors', null);

        const finishFetch = () => {
            if (state.receipts_cancel_source !== cancelSource) {
                return;
            }
            commit('updateReceiptsCancelSource', null);
            commit('updateReceiptsLoading', false);
            commit('updateReceiptsLoaded', true);
            dispatch('fetchReceiptsStats');
        };

        try {
            const response = await axios.get('/api/receipts/', {
                params: state.receipts_filters,
                cancelToken: cancelSource.token,
            });
            if (state.receipts_cancel_source !== cancelSource) {
                return;
            }
            commit('updateReceipts', response.data.results);
            commit('updateReceiptsCount', response.data.count);
            finishFetch();
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            if (state.receipts_cancel_source !== cancelSource) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateReceiptsLoadingErrors', error.details);
            finishFetch();
            throw error;
        }
    },

    resetReceiptsList({ commit }) {
        commit('updateReceiptsLoading', true);
        commit('updateReceiptsLoaded', false);
        commit('updateReceipts', []);
        commit('updateReceiptsCount', 0);
    },

    async fetchReceiptsStats({ commit, state }) {
        commit('updateReceiptsStatsLoading', true);
        commit('updateReceiptsStatsLoadingErrors', null);

        if (state.receipts_stats_cancel_source) {
            state.receipts_stats_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateReceiptsStatsCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/receipts/stats/', {
                params: withSelectedIds(state.receipts_filters, state.selected_items),
                cancelToken: state.receipts_stats_cancel_source?.token
            });
            commit('updateReceiptsStats', response.data);
            commit('updateReceiptsStatsCancelSource', null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateReceiptsStatsLoadingErrors', error.details);
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateReceiptsStatsLoading', false);
        }
    },

    async placeReceipt({ commit, dispatch, state }, params) {
        const url = `/api/receipts/${params.receipt.receipt_id}/place/`;
        try {
            const response = await axios.put(url);
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            dispatch('fetchReceipts');
            dispatch('session/fetchStats', null, { root: true });
        }
    },


    async assignReceipt({ commit, dispatch, state }, params) {
        const url = `/api/receipts/${params.receipt.receipt_id}/assign/`;
        try {
            const response = await axios.put(url, { user: params.user });
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            dispatch('fetchReceipts');
            dispatch('session/fetchStats', null, { root: true });
        }
    },

    updateSelectedItems({ commit }, selected_items) {
        commit('updateSelectedItems', selected_items);
    },

    async bulkAction({ commit, dispatch, state }, params) {
        commit("updateBulkActionLoading", true);
        try {
            const result = await executeBulk({
                bulkActionUrl: "/api/receipts/bulk/",
                action: params.action,
                instances: state.selected_items.map(item => item.id),
                payload: params.payload || {},
                context: params.context,
                getIdFn: params.getIdFn,
                successMessage: params.successMessage,
            });
            const items = state.selected_items;
            commit("updateSelectedItems", []);
            dispatch("fetchReceipts");
            dispatch("session/fetchStats", null, { root: true });
            return { ...result, items };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateBulkActionLoading", false);
        }
    },
};

const getters = {
    /**
     * Filters bound to Table UI: section constraints are hidden so tabs don't
     * surface forced values as user chips. API still uses receipts_filters.
     */
    receipts_table_filters(state) {
        const section = state.section || 'all';
        return omitFilterKeys(
            state.receipts_filters,
            getSectionConstraintKeys(getSectionFilters(section)),
        );
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
